import {
  Currency,
  type CurrencyConstructor,
} from '@webapp/core/entities/external/Currency'
import {
  Approver,
  type ApproverConstructor,
} from '@webapp/core/entities/internal/Approver'
import { type ApproverDelegatee } from '@webapp/core/entities/internal/ApproverDelegatee'
import {
  PrimaryCategory,
  type PrimaryCategoryConstructor,
} from '@webapp/core/entities/internal/PrimaryCategory'
import {
  Requester,
  type RequesterConstructor,
} from '@webapp/core/entities/internal/Requester'
import {
  SecondaryCategory,
  type SecondaryCategoryConstructor,
} from '@webapp/core/entities/internal/SecondaryCategory'
import {
  type ExpenseStatusEnum,
  ExpenseStatuses,
} from '@webapp/modules/expense/entities/enums/ExpenseStatusEnum'
import {
  ExpenseApprovalLog,
  type ExpenseApprovalLogConstructor,
} from '@webapp/modules/expense/entities/ExpenseApprovalLog'
import {
  ExpenseItem,
  type ExpenseItemConstructor,
} from '@webapp/modules/expense/entities/ExpenseItem'
import {
  NextApproverChoice,
  type NextApproverChoiceConstructor,
} from '../NextApproverChoice'

export interface ApprovableExpenseSummaryConstructor {
  id: number
  uuid: string
  title: string
  status: ExpenseStatusEnum
  totalAmount: number
  reimbursableAmount: Nullable<number>
  submittedDate: ISODateString
  currency: CurrencyConstructor
  requester: RequesterConstructor
  primaryCategory: PrimaryCategoryConstructor
  secondaryCategory: SecondaryCategoryConstructor
  nextApprover: Nullable<ApproverConstructor>
  nextApproverChoices: Nullable<NextApproverChoiceConstructor[]>
  items: ExpenseItemConstructor[]
  logs: ExpenseApprovalLogConstructor[]
  delegatedBy: Nullable<string>
  justCreated: boolean
  justUpdated: boolean
}

export class ApprovableExpenseSummary {
  readonly id: number
  readonly uuid: string
  readonly title: string
  readonly status: ExpenseStatusEnum
  readonly totalAmount: number
  readonly reimbursableAmount: number
  readonly submittedDate: Date
  readonly currency: Currency
  readonly requester: Requester
  readonly primaryCategory: PrimaryCategory
  readonly secondaryCategory: SecondaryCategory
  readonly nextApprover: Nullable<Approver>
  readonly nextApproverChoices: NextApproverChoice[]
  readonly items: ExpenseItem[]
  readonly logs: ExpenseApprovalLog[]
  readonly delegatedBy: Nullable<string>
  readonly justCreated: boolean
  readonly justUpdated: boolean

  constructor(data: ApprovableExpenseSummaryConstructor) {
    this.id = data.id
    this.uuid = data.uuid
    this.title = data.title || ''
    this.status = data.status
    this.totalAmount = data.totalAmount || 0
    // API returns null when no reimbursable items; coerced to 0 for display
    this.reimbursableAmount = data.reimbursableAmount || 0
    this.submittedDate = new Date(data.submittedDate)
    this.currency = new Currency(data.currency)
    this.requester = new Requester(data.requester)
    this.primaryCategory = new PrimaryCategory(data.primaryCategory)
    this.secondaryCategory = new SecondaryCategory(data.secondaryCategory)
    this.nextApprover = data.nextApprover
      ? new Approver(data.nextApprover)
      : null
    this.nextApproverChoices = (data.nextApproverChoices || []).map(
      (choice) => new NextApproverChoice(choice)
    )
    this.items = data.items.map((item) => new ExpenseItem(item))
    this.logs = data.logs.map((log) => new ExpenseApprovalLog(log))
    this.delegatedBy = data.delegatedBy || null
    this.justCreated = data.justCreated
    this.justUpdated = data.justUpdated
  }

  get hasItems(): boolean {
    return this.items.length > 0
  }

  get itemCount(): number {
    return this.items.length
  }

  get categoryLabel(): string {
    return `${this.primaryCategory.name} - ${this.secondaryCategory.name}`
  }

  get isPending(): boolean {
    return this.status === ExpenseStatuses.PENDING
  }

  get isApproved(): boolean {
    return this.status === ExpenseStatuses.APPROVED
  }

  get isDenied(): boolean {
    return this.status === ExpenseStatuses.DENIED
  }

  get hasNextApproverChoices(): boolean {
    return this.nextApproverChoices.length > 0
  }

  get hasSingleApproverChoice(): boolean {
    return this.nextApproverChoices.length === 1
  }

  get isApprovalDelegated(): boolean {
    return this.delegatedBy !== null
  }

  get delegatedApprover(): Nullable<ApproverDelegatee> {
    return this.nextApprover?.approvalDelegatee ?? null
  }

  get deniedBy(): Nullable<Approver> {
    if (this.logs.length === 0) return null
    return (
      [...this.logs].reverse().find((log) => log.isDenyAction)?.user ?? null
    )
  }

  get finalApprovedBy(): Nullable<Approver> {
    if (this.logs.length === 0) return null
    return (
      [...this.logs].reverse().find((log) => log.isFinalApprovedAction)?.user ??
      null
    )
  }
}
